import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ParentMessageType } from 'src/app/common/models/ParentMessage';
import { ParentCommunicatorService } from 'src/app/services/parent-communicator.service';
import { PlacemanagementService } from '../services/placemanagement.service';
import { PlaceLabelHelper } from 'src/app/common/helpers/place/place-label-helper';
import { MultimediaPopupInputInfo } from 'src/app/common/models/PopupInputInfo';
import { MultimediaPopupResponse } from 'src/app/common/models/PopupResponse';
import { CropFrameMode, MultimediaPopupOpenMode, MultimediaTypes, PopupResponseEvents } from 'src/app/common/enums/enums';
import { CustomError } from 'src/app/common/helpers/custom-error';
import { LinkRequest } from 'src/app/common/models/LinkRequest';
import { MultimediamanagementService } from 'src/app/components/common/multimedia/services/multimediamanagement.service';
import { first } from 'rxjs/operators';
import { PlaceViewModel } from 'src/app/common/models/PlaceViewModel';
import { PlaceAPIService } from 'src/app/services/API/place-api.service';
import { LoadingService } from 'src/app/services/UI/loading.service';
import { GuidGenerator } from 'src/app/common/helpers/guid-generator';
import { ConfirmationInputInfo } from 'src/app/common/models/ConfirmationInputInfo';
import { ConfirmationContent } from 'src/app/common/models/ConfirmationContent';
import { ConfirmationDialogService } from 'src/app/services/UI/confirmation-dialog.service';
import { Subject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TranslateHandler } from 'src/app/common/helpers/translate-handler';

@Component({
    selector: 'app-place-editor',
    templateUrl: './place-editor.component.html',
    styleUrls: ['./place-editor.component.scss'],
    standalone: false
})
export class PlaceEditorComponent implements OnInit, OnDestroy {

  //Variable Declarations 
  isDropDownOpen = false
  dropdownList: any;
  selectedPlaceType: any;
  multimediaSubscription: any;
  confirmationServiceSubscription: any;
  timeTag: string = "";
  showValidation = false

  //get input from main place popup
  //Decorator Declarations 
  @Input() selectedPlace: PlaceViewModel
  @Output() cancel = new EventEmitter<any>();
  @Output() updatePlaceEvent = new EventEmitter<any>();
  @Output() changePlaceList = new EventEmitter<any>();
  @Output() updateImage = new EventEmitter<any>();


  constructor(private parentCommunicatorService: ParentCommunicatorService,
    private multimediamanagementService: MultimediamanagementService,
    private apiPlaceService: PlaceAPIService,
    private loadingService: LoadingService,
    private confirmationDialogService: ConfirmationDialogService,
    private translateService: TranslateService,
    private translateHandler: TranslateHandler) { }

  ngOnInit(): void {
    this.dropdownList = PlaceLabelHelper.placeTypes;
    this.selectedPlaceType = this.selectedPlace.type ? PlaceLabelHelper.placeTypes.find(t => t.id === this.selectedPlace.type) : PlaceLabelHelper.placeTypes[0];
  }

  // unsubscribe subscriptions
  ngOnDestroy(): void {
    if (this.multimediaSubscription)
      this.multimediaSubscription.unsubscribe();
  }

  //To open multimedia panel for upload an image
  uploadPlaceImage() {
    this.isDropDownOpen = false
    let popupInputData = new MultimediaPopupInputInfo();
    popupInputData.level = 1;
    popupInputData.Title = this.translateHandler.translate('lbl_profile_picture_of', (this.selectedPlace.id && this.selectedPlace.id > 0) ? [this.selectedPlace.fullAddress] : [this.createFullAddress()]);
    popupInputData.PopUpTitle = 'lbl_all_media';
    popupInputData.Mode = MultimediaPopupOpenMode.List;
    popupInputData.FilterMode = [MultimediaTypes.PHOTO];
    popupInputData.CropFrameMode = CropFrameMode.Circle;
    popupInputData.AcceptedMultimediaTypes = [MultimediaTypes.PHOTO];
    popupInputData.SelectedImageIds = popupInputData.SetImageIds(this.selectedPlace.imageId);

    popupInputData.PopupWidth = '43%'

    // Opens the multimedia panels and selection of the media or cancel steps
    this.multimediaSubscription = this.multimediamanagementService.openPopup(popupInputData)
      .pipe(first())
      .subscribe((selectedImages: MultimediaPopupResponse) => {
        if (selectedImages.Status == PopupResponseEvents.Error) {
          throw new CustomError("Image url is invalid", 611, false);
        }
        //Update place profile image if new imaege "selected"
        if ((selectedImages.Status == PopupResponseEvents.Selected ||
          selectedImages.Status == PopupResponseEvents.DeSelected) && selectedImages.Data) {
          if (selectedImages.Status == PopupResponseEvents.DeSelected) {
            selectedImages.Data[0].mediaUrl = null;
            selectedImages.Data[0].id = null;
          }
          let linkRequest = new LinkRequest;
          linkRequest.TargetId = selectedImages.Status === PopupResponseEvents.Selected ? selectedImages.Data[0].id : null;

          if (this.selectedPlace.id && this.selectedPlace.id > 0) {
            this.linkImageByPlaceId(this.selectedPlace.id, linkRequest, selectedImages);
          }
          else {
            this.setselectedPlaceMedia(selectedImages.Data[0])
            this.createPlace().subscribe((res: any) => {
              this.changePlaceList.emit(res);
            });
          }
        }
      },
        (err) => {
          throw new CustomError("PlaceEditorComponent => uploadPlaceImage() : " + err, 911, false);
        }
      )
  }

  // Save place data if  entered data is valid
  savePlace() {
    (this.selectedPlace.id && this.selectedPlace.id > 0) ?
      this.updatePlace() :
      this.createPlace().subscribe((res: any) => {
        this.updatePlaceEvent.emit(res)
      });
  }

  createPlace() {
    let processId = GuidGenerator.generate();
    this.loadingService.show(processId);
    var subject = new Subject<any>();
    this.selectedPlace.type = this.selectedPlaceType.id;
    this.apiPlaceService.createplaceData(this.selectedPlace).subscribe(placeData => {
      this.selectedPlace = placeData.data;
      this.loadingService.hide(processId);
      subject.next(placeData.data);
    }, err => {
      this.loadingService.hide(processId);
      throw new CustomError("PlaceEditorComponent => createPlace() :" + err, 911, false);
    })

    return subject.asObservable();
  }

  updatePlace() {
    let processId = GuidGenerator.generate();
    this.loadingService.show(processId);
    this.selectedPlace.type = this.selectedPlaceType.id;
    this.apiPlaceService.updatePlaceData(this.selectedPlace.id, this.selectedPlace).subscribe(placeData => {
      this.selectedPlace = placeData.data
      this.loadingService.hide(processId);
      this.updatePlaceEvent.emit(placeData.data)

    }, err => {
      this.loadingService.hide(processId);
      throw new CustomError("PlaceEditorComponent => updatePlace() : " + err, 911, false);
    })
  }

  //link image to selected place by Id
  linkImageByPlaceId(placeId: number, linkRequest: LinkRequest, selectedImages) {
    let processId = GuidGenerator.generate();
    this.loadingService.show(processId);
    this.apiPlaceService.updatePlaceMultimediaLink(linkRequest, placeId).subscribe(
      () => {
        this.timeTag = "&timeTag=" + new Date().getTime(); // used to avoid caching         
        this.setselectedPlaceMedia(selectedImages.Data[0])
        this.changePlaceList.emit(this.selectedPlace);
        this.loadingService.hide(processId);
      },
      (err) => {
        this.loadingService.hide(processId);
        throw new CustomError("PlaceEditorComponent => linkImageByPlaceId() : " + err, 911, false);
      }
    )
  }

  //Unlink image to selected place by Id
  unlinkImageByPlaceId(placeId: number, imageId: number) {
    let processId = GuidGenerator.generate();
    this.loadingService.show(processId);
    this.apiPlaceService.unlinkePlaceImage(placeId, imageId)
      .pipe(first())
      .subscribe((response: any) => {
        this.loadingService.hide(processId);
        this.selectedPlace.imageId = -1
        this.selectedPlace.mediaUrl = ''
        this.selectedPlace.originalMediaUrl = ''
        this.selectedPlace.mediaType = 0
        this.isDropDownOpen = false;
        this.changePlaceList.emit(this.selectedPlace);

      },
        (err) => {
          this.loadingService.hide(processId);
          throw new CustomError("PlaceEditorComponent => unlinkImageByPlaceId() : " + err, 911, false);
        });
  }

  //show image unlink confirmation popup
  showImageUnlinkConfirmation() {
    let confirmationDialogHeader = 'lbl_unlink_confirmation_header';
    let confirmationDialogfooter = 'cnf_unlink_confirmation';
    let confirmationDialogBody = this.translateService.instant('cnf_unlink_place_confirmation_message');
    let popupInputData = new ConfirmationInputInfo();
    popupInputData.Visibility = true;
    let confirmationContent = new ConfirmationContent();
    confirmationContent.ConfirmationDialogHeader = confirmationDialogHeader;
    confirmationContent.ConfirmationDialogBody = confirmationDialogBody;
    confirmationContent.ConfirmationDialogFooter = confirmationDialogfooter;
    popupInputData.ConfirmationContent = confirmationContent;
    this.confirmationServiceSubscription = this.confirmationDialogService.OpenPopup(popupInputData)
      .pipe(first())
      .subscribe((response: boolean) => {
        if (response) {
          this.unlinkImageByPlaceId(this.selectedPlace.id, this.selectedPlace.imageId);
        }
      }, (err) => {
        throw new CustomError("PlaceEditorComponent => showImageUnlinkConfirmation() : " + err, 911, false);
      }
      );
  }


  //Set Media to the place
  setselectedPlaceMedia(updated) {
    this.selectedPlace.mediaType = updated.mediaType;
    this.selectedPlace.mediaUrl = updated.mediaUrl;
    this.selectedPlace.imageId = updated.id;
  }
  // For remove place image
  removePlaceImage() {
    this.showImageUnlinkConfirmation();
  }

  // Show dropdown in image to edit and delete
  showDropDown() {
    this.isDropDownOpen = !this.isDropDownOpen;
  }

  // For change place type
  selectPlaceType() {
    this.selectedPlace.type = this.selectedPlaceType.id;
  }

  // Cancel place editor and redirect to place list
  canclePopup() {
    this.cancel.emit()
  }

  createFullAddress() {
    let addressLines = new Array<String>();

    if (this.selectedPlace.detail)
      addressLines.push(this.selectedPlace.detail);
    if (this.selectedPlace.line2)
      addressLines.push(this.selectedPlace.line2);
    if (this.selectedPlace.line3)
      addressLines.push(this.selectedPlace.line3);
    if (this.selectedPlace.line4)
      addressLines.push(this.selectedPlace.line4);
    if (this.selectedPlace.line5)
      addressLines.push(this.selectedPlace.line5);
    if (this.selectedPlace.country)
      addressLines.push(this.selectedPlace.country);

    return addressLines.join(",");
  }


  //Required for future references
  // uploadNewPlaceImage() {
  //   if ( this.selectedPlace.detail || this.selectedPlace.line2 || this.selectedPlace.line3 || this.selectedPlace.line4 || this.selectedPlace.line5 || this.selectedPlace.country) {
  //     this.showValidation = false
  //     this.uploadPlaceImage()
  //     console.log('uploadNewPlaceImage')
  //   } else {
  //     console.log('showValidation')
  //     this.showValidation = true
  //   }
  // }

}
